import React, {useEffect, useState, useRef} from 'react';
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import Cookies from "js-cookie";
import axios from "axios";
import {useDispatch, useSelector} from "react-redux";
import {stringToColor} from "../utils/utils";
import { Star, StarOff, Link2 } from 'lucide-react';
import ItemDetailsModal from "./ItemDetailsModal";
import {configureKeyboardNavigation} from "../redux/actions/global.action";
import { cn } from "@/lib/utils";

// Zustand Store
import useCanvasStore from "../stores/canvasStore";

import { BACKEND_API_URL } from "../utils/api";

/**
 * LinkItem - Displays a link in the canvas
 * 
 * Props (minimal - reads most data from store):
 * - id: link ID
 * - depth: depth level in the canvas
 * - itemIndex: index in the items array for keyboard navigation
 * - path_string: optional path string for search results
 */
const LinkItem = ({
    id,
    depth,
    itemIndex,
    path_string
}) => {
    const dispatch = useDispatch();

    // User settings from Redux (auth)
    const user_settings = useSelector((state) => state.auth.user_settings);
    const isMinimalView = user_settings?.minimal_mode;

    // Read item data and state from Zustand store
    const item = useCanvasStore(s => s.items[depth]?.find(i => i.id === id));
    const selectedPath = useCanvasStore(s => s.selectedPath);
    const selectedItems = useCanvasStore(s => s.selectedItems);
    const focusedIndex = useCanvasStore(s => s.focusedIndex);
    const openDetailPaneId = useCanvasStore(s => s.openDetailPaneId);
    const toggleItemSelection = useCanvasStore(s => s.toggleItemSelection);
    const selectItemRange = useCanvasStore(s => s.selectItemRange);
    const setFocusedIndex = useCanvasStore(s => s.setFocusedIndex);
    const setCurrentDepth = useCanvasStore(s => s.setCurrentDepth);
    const setOpenDetailPaneId = useCanvasStore(s => s.setOpenDetailPaneId);

    // Local state
    const [openModal, setOpenModal] = useState(false);
    const [linkData, setLinkData] = useState(null);
    const nameRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    // Derived values from store
    const data = item || {};
    const name = data.name || '';
    const href = data.link || '';
    const is_archived = data.is_archived || false;

    // Check if this item is focused via keyboard navigation
    const isFocused = focusedIndex === `${depth}_${itemIndex}`;
    
    // Check if this link is selected for multi-select
    const isSelected = selectedItems.some(item => item.id === id);

    // Check if selected in path
    const selected = selectedPath && selectedPath.includes(id);

    const fetchLinkDetails = async () => {
        try {
            const access_token = Cookies.get("access_token")
            const response = await axios.get(`${BACKEND_API_URL}/api/link/${id}`, {
                headers: {
                    'Authorization': `Token ${access_token}`,
                    'Content-Type': 'application/json',
                },
            });
            const linkData = response.data;
            setLinkData(linkData);
        } catch (error) {
            console.error('Error fetching link details:', error);
        }
    };
    
    useEffect(() => {
        if (openModal) {
            fetchLinkDetails();
        }
    }, [openModal]);

    useEffect(() => {
        const checkTruncation = () => {
            if (nameRef.current) {
                setIsTruncated(nameRef.current.scrollWidth > nameRef.current.clientWidth);
            }
        };
        checkTruncation();
        window.addEventListener('resize', checkTruncation);
        return () => window.removeEventListener('resize', checkTruncation);
    }, [name, href]);

    // Watch openDetailPaneId from Zustand and open modal when this item's ID matches
    useEffect(() => {
        if (openDetailPaneId === id && !openModal) {
            setOpenModal(true);
            dispatch(configureKeyboardNavigation(false));
        }
    }, [openDetailPaneId, id, openModal, dispatch]);
    
    const handleLinkClick = (e) => {
        // Set focus to this item on any click
        if (itemIndex !== undefined && depth !== undefined) {
            setFocusedIndex(`${depth}_${itemIndex}`);
            setCurrentDepth(depth);
        }
        
        // Multi-select mode: Ctrl+Click (or Cmd+Click on Mac) - toggle single item
        if ((e.ctrlKey || e.metaKey) && !e.shiftKey) {
            e.preventDefault();
            e.stopPropagation();
            toggleItemSelection({
                id: id,
                parent_id: data.parent_id,
                depth: depth,
                item_type: 'link',
                name: name || href,
                is_archived: is_archived,
                employees: data.employees || []
            }, depth, itemIndex);
            return;
        }
        
        // Range select: Shift+Click (with or without Ctrl) - select all items from anchor to clicked
        if (e.shiftKey) {
            e.preventDefault();
            e.stopPropagation();
            selectItemRange({
                id: id,
                parent_id: data.parent_id,
                item_type: 'link',
                name: name || href,
                is_archived: is_archived,
                employees: data.employees || []
            }, depth, itemIndex);
            return;
        }
        
        // Double-click to open link
        if (e.detail === 2 && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            window.open(href, '_blank');
            return;
        }
    };

    const handleOpenModal = (e) => {
        e.preventDefault();
        if (e.button === 2) { // Right-click
            setOpenModal(true);
            dispatch(configureKeyboardNavigation(false))
        }
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setOpenDetailPaneId(null); // Clear the detail pane ID when closing
        dispatch(configureKeyboardNavigation(true));
    };

    const handleUpdateLinkData = (updatedData) => {
        setLinkData(updatedData);
    };

    // Don't render if item doesn't exist in store
    if (!item) return null;

    return (
        <>
            <div data-name="link-item-container" className="flex w-full">
                <div 
                    data-name="link-item-main"
                    data-item-id={id}
                    onClick={handleLinkClick}
                    onContextMenu={handleOpenModal}
                    className={cn(
                        "folder-container w-full flex",
                        isSelected && "selected",
                        selected && !isSelected && "bg-muted",
                        isFocused && "ring-2 ring-primary ring-offset-1"
                    )}
                >
                {/* Left content */}
                <div className="flex-1 min-w-0 flex flex-col items-start">
                    {/* Header row */}
                    <div className="flex items-center gap-1 w-full overflow-hidden">
                        <span className="flex-shrink-0">
                            {data.star_level > 0 ? 
                                <Star className={cn(
                                    "w-4 h-4 fill-current",
                                    is_archived ? "text-muted-foreground" :
                                    data.star_level === 3 ? "text-destructive" : 
                                    data.star_level === 2 ? "text-warning" : "text-success"
                                )}/> : 
                                <StarOff className={cn(
                                    "w-4 h-4",
                                    is_archived ? "text-muted-foreground" : "text-muted-foreground"
                                )}/>
                            }
                        </span>
                        <Link2 className={cn(
                            "w-4 h-4 flex-shrink-0",
                            is_archived ? "text-muted-foreground" : "text-foreground"
                        )}/>
                        <div className="min-w-0 flex-1">
                        {isTruncated ? (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span ref={nameRef} className={cn(
                                            "text-sm truncate block cursor-pointer select-none",
                                            is_archived ? "text-muted-foreground" : "text-foreground"
                                        )}>
                                            {name ? name : href}
                                        </span>
                                    </TooltipTrigger>
                                    <TooltipContent 
                                        side="top" 
                                        className="bg-white text-black border border-gray-300 shadow-md font-sans max-w-xs break-words"
                                    >
                                        {name ? name : href}
                                    </TooltipContent>
                                </Tooltip>
                            ) : (
                                <span ref={nameRef} className={cn(
                                    "text-sm truncate block cursor-pointer select-none",
                                    is_archived ? "text-muted-foreground" : "text-foreground"
                                )}>
                                    {name ? name : href}
                                </span>
                            )}
                            {path_string && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span className="text-[10px] text-muted-foreground truncate block select-none cursor-pointer">
                                            {path_string}
                                        </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="bottom-start" className="bg-white text-black border border-gray-300 shadow-md font-sans max-w-xs break-words">
                                        {path_string}
                                    </TooltipContent>
                                </Tooltip>
                            )}
                        </div>
                    </div>
                    
                    {/* Tags row */}
                    {!isMinimalView && data.tags && Array.isArray(data.tags) && data.tags.length > 0 && (
                        <div className="flex flex-wrap gap-0.5 mt-0.5">
                            {data.tags.slice(0, 3).map((tag, index) => (
                                <span 
                                    key={index}
                                    className="inline-flex items-center px-1.5 py-0 text-[9px] rounded border border-border bg-secondary text-secondary-foreground"
                                >
                                    {tag.name}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
                
                {/* Right side - assignee avatar */}
                <div className="flex items-center justify-center ml-1 flex-shrink-0 w-10">
                    {!isMinimalView && data.employees && Array.isArray(data.employees) && data.employees.slice(0, 1).map((employee, index) => (
                        <span
                            key={index}
                            className="w-6 h-6 rounded flex items-center justify-center text-[9px] font-semibold text-white"
                            style={{backgroundColor: stringToColor(employee.first_name + employee.last_name)}}
                        >
                            {employee.first_name.charAt(0)}{employee.first_name.charAt(1) || ''}{employee.last_name.charAt(0)}{data.employees.length > 1 ? '..' : ''}
                        </span>
                    ))}
                </div>
                </div>
            </div>

            {linkData &&
                <ItemDetailsModal isOpen={openModal} handleClose={handleCloseModal} depth={depth} itemData={data}
                                  itemInfo={linkData} href={href}
                                  setItemData={handleUpdateLinkData}
                                  itemType={'link'} fetchItemDetails={fetchLinkDetails}/>}
        </>
    );
};

export default LinkItem;
